import { useTwilio } from '@/contexts/TwilioContext';
import IncomingCallModal from '@/components/IncomingCallModal';
import ActiveCallModal from '@/components/ActiveCallModal';
import CallModal from '@/components/contacts/CallModal';
import { TransferCallModal } from '@/components/calls/TransferCallModal';
import { ParkedClientBanner } from '@/components/calls/ParkedClientBanner';
import { MicrophonePermissionModal } from '@/components/calls/MicrophonePermissionModal';
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCallFinalization } from '@/hooks/useCallFinalization';
import { useMicrophonePermission } from '@/hooks/useMicrophonePermission';
import { useLocation } from 'react-router-dom';
import { useDialerSession } from '@/hooks/usePowerDialer';
export default function TwilioDeviceProvider() {
  const { 
    incomingCall, 
    currentCall, 
    isOnCall,
    isInitiatingCall,
    currentCallTargetNumber,
    currentCallRecordId,
    currentConsultantId,
    callError,
    instantDisconnect,
    clearInstantDisconnect,
    acceptIncomingCall, 
    rejectIncomingCall,
    hangUp,
    toggleMute,
    makeCall,
    transferCall,
    transferParkedToConsultant,
    parkedClientName,
    parkedClientPhone,
    isParked,
    isDialerCall,
  } = useTwilio();

  const { ensureCallFinalized } = useCallFinalization();
  const { 
    showPermissionModal, 
    setShowPermissionModal, 
    ensureMicrophoneAccess 
  } = useMicrophonePermission();
  const location = useLocation();

  // Power Dialer: enquanto houver sessão ativa/pausada, o console próprio cuida da UI
  const { data: dialerSession } = useDialerSession();
  const dialerActive = isDialerCall || !!dialerSession;

  const [contactInfo, setContactInfo] = useState<{
    name?: string;
    phone: string;
  } | null>(null);

  const [callDirection, setCallDirection] = useState<'inbound' | 'outbound'>('outbound');
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [hasCallEnded, setHasCallEnded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [callStatusState, setCallStatusState] = useState<'idle' | 'connecting' | 'ringing' | 'connected' | 'ended' | 'failed'>('idle');
  const [showInitiatingModal, setShowInitiatingModal] = useState(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCallRecordIdRef = useRef<string | null>(null);

  // Mantém ref do último callRecordId para garantir finalização
  useEffect(() => {
    if (currentCallRecordId) {
      lastCallRecordIdRef.current = currentCallRecordId;
    }
  }, [currentCallRecordId]);

  // Helper function to translate error codes
  const getErrorMessageInPortuguese = (code: number, message: string): string => {
    const errorMap: Record<number, string> = {
      10004: 'Sistema no limite de chamadas simultâneas. Aguarde alguns segundos.',
      31005: 'Falha ao conectar ao gateway. Verifique sua conexão.',
      31401: 'Permissão de microfone negada. Clique no cadeado 🔒 na barra de endereço para permitir.',
      31486: 'Número ocupado ou indisponível. Tente novamente.',
      31000: 'Falha de autenticação. Recarregue a página.',
      31201: 'Timeout ao conectar. Verifique sua internet.',
      31204: 'Número inválido. Verifique o formato do telefone.',
      31206: 'Sem saldo na conta Twilio. Contate o administrador.',
      53000: 'Sem permissão para fazer chamadas. Contate o administrador.',
      53001: 'Chamadas para este país não habilitadas. Contate o administrador.',
    };
    
    return errorMap[code] || `Erro na chamada: ${message}`;
  };

  // Control initiation modal display
  useEffect(() => {
    if (isInitiatingCall && !isOnCall && !dialerActive) {
      setShowInitiatingModal(true);
    }
  }, [isInitiatingCall, isOnCall, dialerActive]);

  // Observe callError from context and translate to friendly message
  useEffect(() => {
    if (callError) {
      const friendlyMessage = getErrorMessageInPortuguese(
        callError.code, 
        callError.message
      );
      setErrorMessage(friendlyMessage);
      setCallStatusState('failed');
      
      // Ensure ActiveCallModal doesn't open when there's an error
      setIsCallModalOpen(false);
      setHasCallEnded(false);
      
      // Limpar timeout anterior se existir
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      // Keep modal open for 4 seconds to show error
      errorTimeoutRef.current = setTimeout(() => {
        setShowInitiatingModal(false);
        setCallStatusState('idle');
        setErrorMessage(null);
    }, 10000);
    } else {
      setErrorMessage(null);
    }
    
    // Cleanup
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [callError]);

  // Detecta instant disconnect (chamada rejeitada pela operadora)
  // Abre o ActiveCallModal no estado "Chamada Encerrada" para o usuário ler o erro e adicionar notas
  useEffect(() => {
    if (!instantDisconnect) return;
    if (dialerActive) { clearInstantDisconnect(); return; }

    console.log('⚡ [TwilioDeviceProvider] Instant disconnect detected - opening ActiveCallModal with error');

    const msg = 'Chamada rejeitada pelo destinatário ou número indisponível. Verifique o número e tente novamente.';

    // 1. Fecha o CallModal (modal de discagem)
    setShowInitiatingModal(false);

    // 2. Abre o ActiveCallModal no estado "Chamada Encerrada"
    setErrorMessage(msg);
    setIsCallModalOpen(true);
    setHasCallEnded(true);
    setCallStatusState('ended');

    // 3. Toast como backup visual
    toast.warning('Chamada rejeitada ou número indisponível');

    // 4. Limpa o flag de instant disconnect (sem auto-close - usuário fecha manualmente)
    clearInstantDisconnect();
  }, [instantDisconnect, clearInstantDisconnect, dialerActive]);

  // Update call status based on state
  useEffect(() => {
    if (isInitiatingCall && !isOnCall) {
      setCallStatusState('connecting');
    } else if (isOnCall) {
      setCallStatusState('connected');
    }
  }, [isInitiatingCall, isOnCall]);

  // Listener para evento de iniciar ligação do WhatsApp
  useEffect(() => {
    const handleInitiateCall = async (event: CustomEvent) => {
      const { contactId, phoneNumber, contactName } = event.detail;
      console.log('Initiating call from WhatsApp:', { contactId, phoneNumber, contactName });
      
      try {
        // Verifica permissão de microfone antes de fazer a chamada
        const hasAccess = await ensureMicrophoneAccess();
        if (!hasAccess) {
          console.log('🎤 Microphone access denied, showing permission modal');
          return;
        }
        
        await makeCall(phoneNumber, contactId);
      } catch (error) {
        console.error('Error making call:', error);
      }
    };

    window.addEventListener('initiate-call' as any, handleInitiateCall as any);

    return () => {
      window.removeEventListener('initiate-call' as any, handleInitiateCall as any);
    };
  }, [makeCall, ensureMicrophoneAccess]);

  // Listener para evento de permissão de microfone negada (disparado pelo TwilioContext)
  useEffect(() => {
    const handleMicrophoneDenied = () => {
      console.log('🎤 Received microphone-permission-denied event');
      setShowPermissionModal(true);
    };

    window.addEventListener('microphone-permission-denied', handleMicrophoneDenied);

    return () => {
      window.removeEventListener('microphone-permission-denied', handleMicrophoneDenied);
    };
  }, [setShowPermissionModal]);

  // Power Dialer: o console próprio cuida da UI da chamada — fecha/impede modais globais
  useEffect(() => {
    if (dialerActive) {
      setIsCallModalOpen(false);
      setShowInitiatingModal(false);
      setHasCallEnded(false);
    }
  }, [dialerActive]);

  // Gerencia abertura do modal quando chamada está ativa
  useEffect(() => {
    // Se estiver com cliente em espera, fecha o modal e NÃO deixa abrir
    // Esta verificação deve vir PRIMEIRO para ter prioridade
    if (dialerActive) {
      if (isCallModalOpen) {
        setIsCallModalOpen(false);
        setHasCallEnded(false);
      }
      return;
    }

    if (isParked) {
      if (isCallModalOpen) {
        setIsCallModalOpen(false);
        setHasCallEnded(false);
      }
      return; // ← IMPORTANTE: Sai do useEffect, não executa mais nada
    }
    
    // Only open ActiveCallModal if no error AND not parked
    if (isOnCall && !errorMessage) {
      setIsCallModalOpen(true);
      setHasCallEnded(false);
    } else if (isCallModalOpen && !hasCallEnded && !errorMessage) {
      // Chamada terminou mas modal ainda aberto - marca como finalizada
      setHasCallEnded(true);
    }
  }, [isOnCall, isCallModalOpen, hasCallEnded, errorMessage, isParked, dialerActive]);

  // C1) Watchdog: desync detection - if not on call and no current call, close modals
  // MAS NÃO fecha se a chamada terminou normalmente (hasCallEnded = true) - usuário está adicionando notas
  useEffect(() => {
    if (!isOnCall && !currentCall && !isInitiatingCall) {
      // Small delay to avoid race conditions during call setup/teardown
      const timeout = setTimeout(() => {
        // Só fecha se NÃO estiver no estado "chamada terminou" (hasCallEnded = true)
        if (isCallModalOpen && !hasCallEnded) {
          console.log('[Twilio] ⚠️ Desync detected: not on call but modal open - closing');
          setIsCallModalOpen(false);
          setHasCallEnded(false);
        }
        if (showInitiatingModal && !errorMessage) {
          console.log('[Twilio] ⚠️ Desync detected: not initiating but modal open - closing');
          setShowInitiatingModal(false);
        }
        if (callStatusState !== 'idle' && callStatusState !== 'failed') {
          setCallStatusState('idle');
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isOnCall, currentCall, isInitiatingCall, isCallModalOpen, showInitiatingModal, callStatusState, errorMessage, hasCallEnded]);

  // C2) Close modals on route change (if not on active call)
  // MAS NÃO fecha se a chamada terminou e usuário está adicionando notas
  useEffect(() => {
    if (!isOnCall && !isInitiatingCall && !hasCallEnded) {
      if (isCallModalOpen) {
        console.log('[Twilio] 🧭 Route changed, closing call modal');
        setIsCallModalOpen(false);
        setHasCallEnded(false);
      }
      if (showInitiatingModal && !errorMessage) {
        console.log('[Twilio] 🧭 Route changed, closing initiating modal');
        setShowInitiatingModal(false);
      }
      setCallStatusState('idle');
      setContactInfo(null);
    }
  }, [location.pathname]); // Only trigger on route change

  const handleCloseCallModal = useCallback(async () => {
    // Garante que a chamada seja finalizada no banco antes de fechar o modal
    const callIdToFinalize = lastCallRecordIdRef.current;
    
    setIsCallModalOpen(false);
    setHasCallEnded(false);
    setContactInfo(null);
    setErrorMessage(null);
    setCallStatusState('idle');
    
    // Finaliza a chamada se ainda estiver pendente
    if (callIdToFinalize && !isOnCall) {
      console.log('🔧 Finalizing call on modal close:', callIdToFinalize);
      await ensureCallFinalized(callIdToFinalize);
    }
  }, [ensureCallFinalized, isOnCall]);

  const handleCloseInitiatingModal = useCallback(async () => {
    // Clear timeout if user closes manually
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    
    const callIdToFinalize = lastCallRecordIdRef.current;
    
    setShowInitiatingModal(false);
    setErrorMessage(null);
    setCallStatusState('idle');
    hangUp();
    
    // Finaliza a chamada se ainda estiver pendente
    if (callIdToFinalize) {
      console.log('🔧 Finalizing call on initiating modal close:', callIdToFinalize);
      await ensureCallFinalized(callIdToFinalize);
    }
  }, [hangUp, ensureCallFinalized]);

  // Busca informações do contato quando há uma chamada ativa
  useEffect(() => {
    const fetchContactInfo = async () => {
      if (!currentCall) {
        // Não limpa mais o contactInfo aqui - só limpa quando fechar o modal
        return;
      }

      const direction = currentCall.customParameters?.get('direction') || 'outbound';
      setCallDirection(direction as 'inbound' | 'outbound');

      console.log('Fetching contact info:', {
        direction,
        To: currentCall.parameters.To,
        From: currentCall.parameters.From,
        currentCallTargetNumber,
        allParams: currentCall.parameters
      });

      // Para chamadas de saída, usa o número armazenado no contexto (mais confiável)
      // Para chamadas de entrada, busca pelo número "From" mas normaliza primeiro
      let searchNumber: string | null = null;

      if (direction === 'outbound') {
        searchNumber = currentCallTargetNumber;
      } else {
        // Inbound: normalize From (remove "client:" prefix if exists)
        let rawFrom = currentCall.parameters.From;
        if (rawFrom?.startsWith('client:')) {
          // É chamada interna, será tratado abaixo
          searchNumber = rawFrom;
        } else if (rawFrom) {
          // Remove any non-numeric characters and add + if not present
          searchNumber = rawFrom.replace(/[^\d+]/g, '');
          if (!searchNumber.startsWith('+')) {
            searchNumber = '+' + searchNumber;
          }
        }
      }

      // Se não encontrou o número, retorna
      if (!searchNumber) {
        console.warn('⚠️ Search number is undefined, cannot fetch contact info');
        setContactInfo({
          phone: 'Número desconhecido',
        });
        return;
      }

      console.log('🔍 Searching contact with number:', searchNumber);

      // If starts with "client:", it's always an internal user (app_user)
      if (searchNumber?.startsWith('client:')) {
        const authUserId = searchNumber.replace('client:', '');
        const { data: appUser } = await supabase
          .from('app_users')
          .select('first_name, last_name, display_name')
          .eq('auth_user_id', authUserId)
          .maybeSingle();

        if (appUser) {
          setContactInfo({
            name: appUser.display_name || `${appUser.first_name || ''} ${appUser.last_name || ''}`.trim(),
            phone: 'Internal User',
          });
        } else {
          setContactInfo({
            phone: 'Unknown User',
          });
        }
        return;
      }

      // Busca contato pelo número de telefone
      const { data: contact } = await supabase
        .from('contacts')
        .select('first_name, last_name, primary_phone')
        .eq('primary_phone', searchNumber)
        .maybeSingle();

      if (contact) {
        setContactInfo({
          name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
          phone: searchNumber,
        });
      } else {
        // DEPRECATED - leads fallback removed
        setContactInfo({
          phone: searchNumber || 'Número desconhecido',
        });
      }
    };

    fetchContactInfo();
  }, [currentCall, currentCallTargetNumber]);

  return (
    <>
      {/* Modal para chamadas entrantes (antes de aceitar) */}
      <IncomingCallModal 
        call={incomingCall}
        onAccept={acceptIncomingCall}
        onReject={rejectIncomingCall}
      />
      
      {/* Modal para chamada em andamento (discando) */}
      {showInitiatingModal && !isOnCall && !dialerActive && currentCallTargetNumber && (
        <CallModal
          isOpen={true}
          onClose={handleCloseInitiatingModal}
          contactName={contactInfo?.name}
          contactPhone={contactInfo?.phone || currentCallTargetNumber}
          callStatus={callStatusState}
          errorMessage={errorMessage}
          onEndCall={handleCloseInitiatingModal}
          onToggleMute={toggleMute}
          isMuted={false}
        />
      )}

      {/* Modal para chamadas ativas (após aceitar/fazer chamada) */}
      {isCallModalOpen && !dialerActive && (
        <ActiveCallModal
          isOpen={isCallModalOpen}
          onClose={handleCloseCallModal}
          contactName={parkedClientName || contactInfo?.name}
          contactPhone={parkedClientPhone || contactInfo?.phone || currentCallTargetNumber || 'Número desconhecido'}
          direction={callDirection}
          callRecordId={currentCallRecordId}
          isCallActive={isOnCall}
          onEndCall={hangUp}
          onToggleMute={toggleMute}
          onTransfer={() => setIsTransferModalOpen(true)}
          onTransferParked={async () => {
            if (currentConsultantId) {
              await transferParkedToConsultant(currentConsultantId);
            } else {
              toast.error("Não foi possível identificar o consultor");
            }
          }}
          isMuted={currentCall?.isMuted()}
          errorMessage={errorMessage}
          parkedClientName={parkedClientName}
          parkedClientPhone={parkedClientPhone}
        />
      )}

      {/* Modal de transferência de chamada */}
      <TransferCallModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onTransfer={transferCall}
      />

      {/* Banner global de cliente em espera */}
      <ParkedClientBanner />

      {/* Modal de permissão de microfone */}
      <MicrophonePermissionModal
        open={showPermissionModal}
        onOpenChange={setShowPermissionModal}
        onRetry={ensureMicrophoneAccess}
      />
    </>
  );
}
